import React, {ReactElement} from "react";
import dayjs from "dayjs";
import {default as ReactDatePicker} from "react-date-picker";

import Label from "../label/Label";
import ErrorMessage from "../error-message/ErrorMessage";
import {BaseInput} from "/src/types";
import Icon from "../../icon/Icon";
import HelpCircleIcon from "/src/svg/help-circle.svg";
import CheckIcon from "/src/svg/check.svg";
import FormErrorIcon from "/src/svg/form-error.svg";

import translate from "/src/utils/translate";
import {css} from "/src/utils/utils";

import {LOCALE} from "/src/config";

import "react-date-picker/dist/DatePicker.css";
import "react-calendar/dist/Calendar.css";
import "./date-picker-overwrite.scss";
import * as styles from "./date-picker.module.scss";

interface DatePickerProps extends Omit<BaseInput, "value"> {
  value?: Date;
  minDate?: Date;
  maxDate?: Date;
  onChange?: (change: string) => unknown;
  transparentBg?: boolean;
  disableError?: boolean;
  format?: string;
  valueFormat?: string;
}

const DatePicker = (props: DatePickerProps): ReactElement => {
  const {
    label = "",
    required = false,
    name,
    value = undefined,
    errorMessage = "",
    status,
    disabled = false,
    readOnly = false,
    transparentBg = false,
    onChange,
    minDate = new Date(),
    // default hundred years from current date
    maxDate = new Date(new Date().setFullYear(new Date().getFullYear() + 100)),
    darkMode = false,
    disableError = false,
    tooltipContent,
    tooltipIcon = HelpCircleIcon,
    format = "dd / MM / yyyy",
    // expected selected date format
    valueFormat,
  } = props;

  const _getFormattedVal = (val: Date): string => {
    return valueFormat != null
      ? dayjs(val).format(valueFormat)
      : val.toLocaleDateString();
  };

  const labelId = `${name}_label`;
  const [isFocusedState, setIsFocusedState] = React.useState<boolean>(false);
  const [selectedValue, setSelectedValue] = React.useState<Date>(
    value || new Date()
  );

  const [selectedValueStr, setSelectedValueStr] = React.useState(
    _getFormattedVal(value || new Date())
  );
  const [isMounted, setIsMounted] = React.useState(false);

  const isInvalid = status === "error";
  const isValid = status === "success";

  const onDatePickerChange = (val: Date) => {
    setSelectedValue(val);

    setSelectedValueStr(_getFormattedVal(val));

    if (onChange) {
      onChange(_getFormattedVal(val));
    }
  };

  const onCalendarOpen = () => {
    // if status is not error/success, set focus state
    if (!isInvalid && !isValid) {
      setIsFocusedState(true);
    }
  };

  const onCalendarClose = () => {
    // if status is not error/success, set focus state
    if (!isInvalid && !isValid) {
      setIsFocusedState(false);
    }
  };

  React.useEffect(() => {
    if (!isMounted) {
      const defaultDateValue = value || new Date();
      setIsMounted(true);
      setSelectedValue(defaultDateValue);

      // Trigger change or value set
      if (onChange) {
        onChange(_getFormattedVal(defaultDateValue));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      data-testid="date-picker"
      className={css(
        // for easy style overwrite
        "date-picker",
        styles.datePicker,
        darkMode ? styles.datePickerDark : "",
        // for easy style overwrite
        darkMode ? "date-picker--dark" : "",
        transparentBg ? styles.datePickerTransparent : "",
        isFocusedState ? styles.datePickerFocus : "",
        isInvalid ? styles.datePickerError : "",
        isValid ? styles.datePickerSuccess : "",
        // for easy style overwrite
        disabled ? "date-picker--disabled" : "",
        disabled ? styles.datePickerDisabled : ""
      )}
    >
      <Label
        id={labelId}
        label={label}
        required={required}
        darkMode={darkMode}
        disabled={disabled}
        tooltipContent={tooltipContent}
        tooltipIcon={tooltipIcon}
      />
      <div
        role="group"
        aria-labelledby={labelId}
        className={css(styles.datePicker__el)}
      >
        <input
          data-testid="date-picker-hidden-input"
          type="hidden"
          name={name}
          value={selectedValueStr}
        />
        <ReactDatePicker
          data-testid="date-picker-picker"
          className={css(styles.datePicker__cal)}
          clearIcon={null}
          value={selectedValue}
          locale={LOCALE}
          disabled={disabled || readOnly}
          required={required}
          calendarAriaLabel={translate("Toggle calendar")}
          dayAriaLabel={translate("Day")}
          monthAriaLabel={translate("Month")}
          yearAriaLabel={translate("Year")}
          format={format}
          showLeadingZeros
          closeCalendar
          onCalendarClose={onCalendarClose}
          onCalendarOpen={onCalendarOpen}
          minDate={minDate}
          maxDate={maxDate}
          openCalendarOnFocus={false}
          // @ts-ignore
          onChange={onDatePickerChange}
        />

        {/* always leave a space for icon */}
        <div className={css(styles.datePicker__iconWrapper)}>
          {(isInvalid || isValid) && (
            <Icon
              svg={isValid ? CheckIcon : FormErrorIcon}
              role="presentation"
              decorations={styles.datePicker__icon}
            />
          )}
        </div>
      </div>
      {!disableError && (
        <ErrorMessage message={errorMessage} visible={isInvalid} />
      )}
    </div>
  );
};

export default DatePicker;
